import 'styled-components';
import { Flex } from '@keyslabor/shared/ui';
import { useQuestionsAnswers } from '@/hooks/useQuestionsAnswers';
import { useInterviewPreview } from '@/api/services';
import { Fragment, useMemo, useRef, useState } from 'react';
import VideoPlayer from '@/components/VideoPlayer';
import { Answer } from '@/api/types';
import styled, { createGlobalStyle } from 'styled-components';
import colors from '@keyslabor/shared/theme/colors';
import { extractQuestionNumber } from '@/utils/format';
import InterviewLeadInfo from "./InterviewLeadInfo.tsx";
import {Question, QuestionWrapper, VideoWrapper} from "./components/Question.tsx";
import {mobileBreakpoint, useIsMobile} from '@keyslabor/shared/hooks/useBreakPoint';
import InterviewCarousel from "./components/InterviewCarousel.tsx";
import InterviewSlider from "./components/InterviewSlider.tsx";
import { px } from "@keyslabor/shared/utils.ts";
import { CollapsibleQuestionAnswer } from './components/CollapsibleQuestionAnswer.tsx';
import { TranslateButton } from '@/pages/InterviewPreview/components/TranslateButton.tsx';
import { useVisibilityActivation } from '@/hooks/useVisibilityActivation';

const GlobalStyle = createGlobalStyle`
  html,
  body,
  #root {
    position: relative;
    overflow: hidden !important;
    scroll-behavior: smooth;
  }
`;

const InterviewPageWrapper = styled(Flex)`
  padding: 0px;
  width: 100%;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: ${colors.grayInterviewPreviewBg};
  overflow: hidden;

  @media screen and (min-width: ${px(mobileBreakpoint)}) {
    height: 100dvh;
    min-height: 100dvh;
    max-height: 100dvh;
    padding-top: 10px;
  }
`;

function InterviewPreview() {
  const { invitationKey } = useQuestionsAnswers();
  const isMobile = useIsMobile();

  const { data } = useInterviewPreview({
    invitation_key: invitationKey,
  });

  const [answers, totalQuestions] = useMemo(() => {
    const answers = data?.answers ?? [];

    const totalQuestions = Math.max(...answers.map((answer) => {
      return extractQuestionNumber(answer?.question_name);
    }))

    return [
        answers.sort(sortAnswersByQuestionName),
        totalQuestions
    ] as const;
  }, [data?.answers]);

  const [isTranslated, setIsTranslated] = useState(false);
  const canBeTranslated =  useMemo(() => {
    const firstAnswer = answers?.[0];
    return firstAnswer?.language !== 'english' && !!firstAnswer?.answer_transcription_english;
  }, [answers]);

  const wrapperRef = useRef<HTMLDivElement>(null);
  const { activatedIndices, slideRef } = useVisibilityActivation(answers.length);

  // @ts-expect-error - GlobalStyle type is not compatible with JSX but works
  const globalStyles = <GlobalStyle />
  const ListRendererComponent = isMobile ? InterviewSlider : InterviewCarousel;

  return (
    <>
      {globalStyles}
      <InterviewPageWrapper ref={wrapperRef}>
        {data && !isMobile ? <InterviewLeadInfo data={data} /> : null}

        {data ? (
            <ListRendererComponent>
              {answers.map((answer, index) => {
                    const questionNumber = extractQuestionNumber(answer?.question_name);
                    const videoSrc = activatedIndices.has(index)
                      ? answer.video_processed_url
                      : undefined;

                    return (
                        <Fragment key={index}>
                          <div ref={slideRef(index)}>
                          {index === 0 ? (
                              <QuestionWrapper>
                                <Flex flexDirection="column" width="100%" alignItems="center" gap="8px" gridRow={isMobile ? "1 / 3": "1 / 2"}>
                                  {isMobile ? <InterviewLeadInfo data={data} /> : 'Introduction'}
                                </Flex>
                                <Flex flexDirection="row" width="100%">
                                  <CollapsibleQuestionAnswer
                                    mt="5px"
                                    width={canBeTranslated ? '270px' : '335px'}
                                    answer={answer}
                                    isTranslated={isTranslated}
                                  />

                                  {canBeTranslated && (
                                    <TranslateButton
                                      originalLang={answer.language ?? 'english'}
                                      isTranslated={isTranslated}
                                      setIsTranslated={setIsTranslated}
                                    />
                                  )}
                                </Flex>
                                <VideoWrapper gridRow={isMobile ? "4 / 4" : "3 / 4"}>
                                  <VideoPlayer src={videoSrc} controls showSlowNetworkWarning />
                                </VideoWrapper>
                              </QuestionWrapper>
                          ) : (
                              <Question
                                answer={answer}
                                questionNumber={questionNumber}
                                totalQuestions={totalQuestions}
                                isTranslated={isTranslated}
                                videoSrc={videoSrc}
                              />
                          )}
                          </div>
                        </Fragment>
                    )
                  }
              )}
            </ListRendererComponent>
        ) : null}
      </InterviewPageWrapper>
    </>
  );
}

const sortAnswersByQuestionName = (a: Answer, b: Answer) => {
  const aNum = extractQuestionNumber(a.question_name);
  const bNum = extractQuestionNumber(b.question_name);
  return aNum - bNum;
};

export default InterviewPreview;
